import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  useMemo,
  type ReactNode,
} from 'react';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface LayoutSlotsContextValue {
  sidebarPortalEl: HTMLDivElement | null;
  headerPortalEl: HTMLDivElement | null;
  setSidebarPortalEl: (el: HTMLDivElement | null) => void;
  setHeaderPortalEl: (el: HTMLDivElement | null) => void;
  setSidebarSlot: (
    title: string | undefined,
    padded?: boolean,
    initialCollapsed?: boolean,
    onToggle?: (collapsed: boolean) => void
  ) => void;
  clearSidebarSlot: () => void;
  setHeaderSlot: (title: string | undefined) => void;
  clearHeaderSlot: () => void;
  sidebarActive: boolean;
  headerActive: boolean;
  sidebarTitle: string | undefined;
  sidebarPadded: boolean;
  headerTitle: string | undefined;
  subSidebarCollapsed: boolean;
  toggleSubSidebar: () => void;
  pageTitle: string | undefined;
  setPageTitle: (title: string) => void;
  clearPageTitle: () => void;
  breadcrumbItems: BreadcrumbItem[];
  setBreadcrumbs: (items: BreadcrumbItem[]) => void;
  clearBreadcrumbs: () => void;
}

const LayoutSlotsContext = createContext<LayoutSlotsContextValue | null>(null);

export function LayoutSlotsProvider({ children }: { children: ReactNode }) {
  const [sidebarPortalEl, setSidebarPortalEl] = useState<HTMLDivElement | null>(
    null
  );
  const [headerPortalEl, setHeaderPortalEl] = useState<HTMLDivElement | null>(
    null
  );

  const [sidebarActive, rawSetSidebarActive] = useState(false);
  const [headerActive, rawSetHeaderActive] = useState(false);
  const [sidebarTitle, rawSetSidebarTitle] = useState<string | undefined>();
  const [sidebarPadded, rawSetSidebarPadded] = useState(true);
  const [headerTitle, rawSetHeaderTitle] = useState<string | undefined>();
  const [subSidebarCollapsed, setSubSidebarCollapsed] = useState(false);
  const [breadcrumbItems, rawSetBreadcrumbItems] = useState<BreadcrumbItem[]>(
    []
  );

  // Stable ref for the per-page toggle callback — avoids dependency churn on
  // consumers and lets each page supply its own persistence handler.
  const onSubSidebarToggleRef = useRef<
    ((collapsed: boolean) => void) | undefined
  >(undefined);

  const setSidebarSlot = useCallback(
    (
      title: string | undefined,
      padded = true,
      initialCollapsed = false,
      onToggle?: (collapsed: boolean) => void
    ) => {
      rawSetSidebarActive(true);
      rawSetSidebarTitle(title);
      rawSetSidebarPadded(padded);
      setSubSidebarCollapsed(initialCollapsed);
      onSubSidebarToggleRef.current = onToggle;
    },
    []
  );

  const clearSidebarSlot = useCallback(() => {
    rawSetSidebarActive(false);
    rawSetSidebarTitle(undefined);
    rawSetSidebarPadded(true);
    onSubSidebarToggleRef.current = undefined;
  }, []);

  const setHeaderSlot = useCallback((title: string | undefined) => {
    rawSetHeaderActive(true);
    rawSetHeaderTitle(title);
  }, []);

  const clearHeaderSlot = useCallback(() => {
    rawSetHeaderActive(false);
    rawSetHeaderTitle(undefined);
  }, []);

  const toggleSubSidebar = useCallback(() => {
    setSubSidebarCollapsed((prev) => {
      const next = !prev;
      onSubSidebarToggleRef.current?.(next);
      return next;
    });
  }, []);

  const setBreadcrumbs = useCallback((items: BreadcrumbItem[]) => {
    rawSetBreadcrumbItems(items);
  }, []);

  const clearBreadcrumbs = useCallback(() => {
    rawSetBreadcrumbItems([]);
  }, []);

  const setPageTitle = useCallback(
    (title: string) => {
      setBreadcrumbs([{ label: title }]);
    },
    [setBreadcrumbs]
  );

  const clearPageTitle = useCallback(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  const pageTitle = useMemo(
    () => breadcrumbItems[breadcrumbItems.length - 1]?.label,
    [breadcrumbItems]
  );

  const value = useMemo(
    () => ({
      sidebarPortalEl,
      headerPortalEl,
      setSidebarPortalEl,
      setHeaderPortalEl,
      setSidebarSlot,
      clearSidebarSlot,
      setHeaderSlot,
      clearHeaderSlot,
      sidebarActive,
      headerActive,
      sidebarTitle,
      sidebarPadded,
      headerTitle,
      subSidebarCollapsed,
      toggleSubSidebar,
      pageTitle,
      setPageTitle,
      clearPageTitle,
      breadcrumbItems,
      setBreadcrumbs,
      clearBreadcrumbs,
    }),
    [
      sidebarPortalEl,
      headerPortalEl,
      setSidebarSlot,
      clearSidebarSlot,
      setHeaderSlot,
      clearHeaderSlot,
      sidebarActive,
      headerActive,
      sidebarTitle,
      sidebarPadded,
      headerTitle,
      subSidebarCollapsed,
      toggleSubSidebar,
      pageTitle,
      setPageTitle,
      clearPageTitle,
      breadcrumbItems,
      setBreadcrumbs,
      clearBreadcrumbs,
    ]
  );

  return (
    <LayoutSlotsContext.Provider value={value}>
      {children}
    </LayoutSlotsContext.Provider>
  );
}

export function useLayoutSlots(): LayoutSlotsContextValue {
  const context = useContext(LayoutSlotsContext);
  if (!context) {
    throw new Error('useLayoutSlots must be used within a LayoutSlotsProvider');
  }
  return context;
}
